import { Anchor, toast } from "common/components/ui";
import {
  BulkProductResolutionRequest,
  EntityError,
  FileExtensionType,
  QuantityUom,
} from "@deliverr/replenishment-client";
import { BulkProductUploadConstants } from "./BulkProductUploadConstants";
import { BULK_UPLOAD_CONTENT_MESSAGES } from "components/BulkUpload/BulkUploadContentMessages";
import { BULK_UPLOAD_ERROR_MESSAGES } from "components/BulkUpload/BulkUploadErrorMessages";
import { FeatureName, useFeatureOn } from "common/Split";
import { getSellerId } from "common/user/UserSelectors";
import { InventoryPool } from "@deliverr/business-types";
import { logError } from "Logger";
import React, { useCallback, useMemo, useState } from "react";
import { replenishmentClient } from "Clients";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { uploadSheet } from "transfers/utils/uploadFile";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { bulkImportProducts } from "./actions/bulkImportProducts";
import { extractEntityErrors } from "../../utils/errorUtils";
interface UploadFile {
  name: string;
  s3FilePath: string;
  fileType: FileExtensionType;
}

const getFileExtensionType = (fileName: string): FileExtensionType =>
  FileExtensionType[fileName.split(".").pop()?.toUpperCase() ?? FileExtensionType.XLSX];

export const getBulkUploadDynamicValues = (error: EntityError) => ({
  ...error.payload?.row,
  entityId: error.entityId,
});

export const useBulkProductUpload = (onClose) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const sellerId = useSelector(getSellerId);
  const { isEachPickingOrder } = useSelector(selectTransferCreate);
  const isDefaultPoolB2BMerchant = useFeatureOn(FeatureName.DefaultPoolB2BMerchant);

  const [fileDetails, setFileDetails] = useState<UploadFile>();
  const [uploadError, setUploadError] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>();
  const [importErrors, setImportErrors] = useState<EntityError[]>([]);
  const [shouldShowDownloadReport, setShouldShowDownloadReport] = useState(false);

  const clearFile = useCallback(async (): Promise<void> => {
    setFileDetails(undefined);
    setUploadError(undefined);
    setShouldShowDownloadReport(false);
    setImportErrors([]);
  }, []);

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      const ctx = { fn: "useBulkProductUpload.handleFileUpload", sellerId };
      const uploadedFile = files[0];

      if (uploadedFile.size > BulkProductUploadConstants.MAX_FILE_SIZE) {
        setUploadError(formatMessage(BULK_UPLOAD_ERROR_MESSAGES.FILE_TOO_BIG));
        return;
      }

      const fileExtensionType = getFileExtensionType(uploadedFile.name);

      setUploadError(undefined);
      setIsUploading(true);
      setLoadingMessage(formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.UPLOADING_FILE));

      try {
        const uploadUrlResponse = await replenishmentClient.requestSheetUploadUrl({
          sellerId,
          fileExtensionType,
          originalFileName: uploadedFile.name,
          fileSize: uploadedFile.size,
        });

        const { url, s3FilePath } = uploadUrlResponse.value;

        await uploadSheet(url, uploadedFile, fileExtensionType.toLowerCase());

        setFileDetails({
          name: uploadedFile.name,
          s3FilePath: s3FilePath.split("/").pop() ?? "",
          fileType: fileExtensionType,
        });
      } catch (error) {
        logError(ctx, error, "Error uploading file for bulk product upload");
        setUploadError(formatMessage(BULK_UPLOAD_ERROR_MESSAGES.FILE_UPLOAD_FAILED));
      } finally {
        setIsUploading(false);
        setLoadingMessage(undefined);
      }
    },
    [formatMessage, sellerId]
  );

  const handleImportProducts = useCallback(async () => {
    setIsImporting(true);
    setLoadingMessage(formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.RESOLVING_PRODUCTS));
    setShouldShowDownloadReport(false);
    const ctx = { fn: "useBulkProductUpload.handleImportProducts", sellerId, fileDetails };

    try {
      if (!fileDetails) {
        logError(ctx, "File is not defined");
        toast.error(formatMessage(BULK_UPLOAD_ERROR_MESSAGES.FILE_MISSING));
        return;
      }
      const bulkProductResolutionRequest: BulkProductResolutionRequest = {
        sellerId,
        s3FilePath: fileDetails.s3FilePath,
        fileType: fileDetails.fileType,
        sheetName: BulkProductUploadConstants.SHEET_TO_PROCESS,
        headerRow: BulkProductUploadConstants.HEADER_ROW,
        inventoryPool: isDefaultPoolB2BMerchant ? InventoryPool.DEFAULT : InventoryPool.STORAGE,
        quantityUom: isEachPickingOrder ? QuantityUom.EACH : QuantityUom.CASE,
      };

      const response = await replenishmentClient.resolveBulkProductUpload(bulkProductResolutionRequest);

      setLoadingMessage(formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.IMPORTING_PRODUCTS));

      const onError = (errors: EntityError[]) => setImportErrors(errors);

      if (await dispatch(bulkImportProducts(response.value, onError))) {
        setImportErrors([]);
        toast.success(formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.PRODUCTS_IMPORTED), {
          autoClose: 5000,
        });
        onClose();
      }
      setShouldShowDownloadReport(true);
    } catch (error) {
      setShouldShowDownloadReport(false);
      setImportErrors(extractEntityErrors(error?.response?.data?.error));
      logError(ctx, error, "Error resolving bulk product upload");
      toast.error(formatMessage(BULK_UPLOAD_ERROR_MESSAGES.UNABLE_TO_IMPORT_PRODUCTS));
    } finally {
      setIsImporting(false);
      setLoadingMessage(undefined);
    }
  }, [fileDetails, sellerId, isEachPickingOrder, isDefaultPoolB2BMerchant, dispatch, formatMessage, onClose]);

  const templateLink = useMemo(
    () => (
      <Anchor href={BulkProductUploadConstants.TEMPLATE_URL}>
        {formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.DOWNLOAD_TEMPLATE_LINK)}
      </Anchor>
    ),
    [formatMessage]
  );

  const instructionsLink = useMemo(
    () => (
      <Anchor href={BulkProductUploadConstants.INSTRUCTIONS_URL}>
        {formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.INSTRUCTIONS_LINK)}
      </Anchor>
    ),
    [formatMessage]
  );

  const modalTitle = useMemo(() => {
    return loadingMessage ?? formatMessage(BULK_UPLOAD_CONTENT_MESSAGES.UPLOAD_FILE);
  }, [loadingMessage, formatMessage]);

  return {
    templateLink,
    instructionsLink,
    modalTitle,
    clearFile,
    fileDetails,
    handleFileUpload,
    uploadError,
    isUploading,
    handleImportProducts,
    isImporting,
    loadingMessage,
    importErrors,
    shouldShowDownloadReport,
  };
};
